









































import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { mapGetters } from 'vuex';

import { Chapter } from 'frontend/types';
import Lesson from 'frontend/views/courses/CoursePage/components/Lesson.vue';
import BriocheBackgroundImage from 'frontend/components/BriocheBackgroundImage.vue';
import ChapterAdmin from 'frontend/views/courses/CoursePage/components/ChapterAdmin.vue';
import { animationsEventBus } from 'frontend/views/courses/CoursePage/utils/animationTimings';
import { safeWindow } from 'frontend/utils/window';

@Component({
  components: { ChapterAdmin, BriocheBackgroundImage, Lesson },
  computed: {
    ...mapGetters('pageHeader', ['isMobile']),
  },
})
export default class ChaptersList extends Vue {
  @Prop({ type: Array, required: true })
  private chapters!: Chapter[];
  @Prop({ type: Number, required: true })
  private courseId!: number;
  @Prop({ type: String, required: true })
  private courseVersionName!: string;
  @Prop({ type: String, default: null })
  private recommendedSlug?: string;
  @Prop({ type: Boolean, default: false })
  private disableAutoScroll!: boolean;
  @Prop({ type: String, default: null })
  private previousLessonSlug?: string;
  @Prop({ type: Boolean, default: false })
  private enableJustCompletedAnimation!: boolean;

  mounted(): void {
    this.scrollToRecommendedChapterIfNeeded();
  }

  private scrollToRecommendedChapterIfNeeded(): void {
    // reset scroll to top of page, Vue scrollBehavior used to handle this
    // but we want to handle scrolling ourselves on this page
    window.scroll({ top: 0 });

    if (!this.disableAutoScroll) {
      const recommendedChapterIndex = this.recommendedSlug
        ? this.chapters.findIndex(chapter =>
            chapter.quizzes.some(lesson => lesson.slug === this.recommendedSlug),
          )
        : -1;

      if (recommendedChapterIndex !== -1) {
        // Disable default scrolling behavior for this page to prevent jumps and allow the scrolling
        // to the recommended chapter. This setting does not affect other pages in our application.
        // Some lines below the default scrolling behavior is restored if we don't have a recommended chapter.
        window.history.scrollRestoration = 'manual';

        const recommendedChapter = (this.$refs.chapters as Element[])[recommendedChapterIndex];
        const chapterFitsInScreen =
          recommendedChapter.getBoundingClientRect().height < window.innerHeight * 0.8;

        if (chapterFitsInScreen && !this.enableJustCompletedAnimation) {
          const expectedPixelsBetweenChapterAndScreenTop = 137;
          const pixelsToScroll =
            document.documentElement.scrollTop +
            recommendedChapter.getBoundingClientRect().top -
            expectedPixelsBetweenChapterAndScreenTop;

          window.scroll({ left: 0, top: pixelsToScroll, behavior: 'smooth' });
        } else if (this.enableJustCompletedAnimation) {
          const previousLesson =
            recommendedChapter.parentElement!.querySelector('.lesson.is-previous');

          if (previousLesson) {
            previousLesson.scrollIntoView({ behavior: 'smooth', block: 'center' });
            this.waitForScrollToBeFinishedToTriggerAnimations();
          }
        } else {
          const recommendedLesson = recommendedChapter.querySelector('.lesson.is-recommended');

          if (recommendedLesson) {
            recommendedLesson.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      } else {
        window.history.scrollRestoration = 'auto';
      }
    }
  }

  private waitForScrollToBeFinishedToTriggerAnimations(): void {
    let doesItHaveToScroll = false;
    let scrollTimeout: NodeJS.Timeout | undefined;
    const listenToScroll = () => {
      doesItHaveToScroll = true;
      preventEventToBeSent(); // If `scroll` is not longer fired, this is not going to be called.
      sendEventInAMomentAndRemoveListener();
    };
    const preventEventToBeSent = () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
    const sendEventInAMomentAndRemoveListener = () => {
      scrollTimeout = setTimeout(() => {
        safeWindow()?.removeEventListener('scroll', listenToScroll);
        animationsEventBus.$emit('start-animations');
      }, 100);
    };

    safeWindow()?.addEventListener('scroll', listenToScroll);

    setTimeout(() => {
      // If the previousLesson is already visible, the scroll is not going to happen,
      // in that case we emit the event here.
      if (!doesItHaveToScroll) {
        safeWindow()?.removeEventListener('scroll', listenToScroll);
        animationsEventBus.$emit('start-animations');
      }
    }, 100);
  }
}
